/**
 * Who is in the valley, where they are, and what they are doing: the pure
 * half of apps/display/public/avatar-residents.js.
 *
 * V12: nobody walks and nobody changes activity while you watch. Each child
 * is dealt one activity for the period -- a fresh set at dawn and another at
 * dusk -- sits or stands at that activity's station, and loops there. The deal
 * is a pure function of the roster and the period, so every device shows the
 * same valley and a reload changes nothing.
 *
 * The display's DOM half (sync/render) is LivingValley's `Residents` here;
 * residents.parity.test.ts holds this file to the display's `cast`.
 */

import type { AvatarSpec } from './avatar';
import { assignRoster, BY_ID, capacity, drawWidth, layout, STATIONS, type Activity, type Placed } from './activities';

/** Seconds before a larger roster takes turns. */
export const ROTATE = 120;
/** The most a wall holds; a phone holds fewer. Rosters rotate. */
export const ON_SCREEN = 4;

export interface Resident {
  id: string;
  name?: string;
  avatar: AvatarSpec;
}

/** Who may appear: approved, with a character. */
export function eligible<T extends { status?: string; avatar?: AvatarSpec | null }>(roster: T[]): Array<T & { avatar: AvatarSpec }> {
  return roster.filter((person) => person.status === 'approved' && person.avatar) as Array<T & { avatar: AvatarSpec }>;
}

/** Everyone on screen this turn, placed and pulled apart. */
export function cast<P extends { id: string; name?: string }>(
  people: P[],
  width: number,
  height: number,
  night: boolean,
  turn = 0,
  now: number | Date | null = null,
): { shown: P[]; assignments: Map<string, Activity>; placed: Placed[] } {
  const room = Math.min(capacity(width, height), ON_SCREEN, people.length);
  const shown: P[] = [];
  for (let i = 0; i < room; i++) shown.push(people[(i + turn) % people.length]!);
  const assignments = assignRoster(shown, now, night);
  return { shown, assignments, placed: layout(shown, assignments, width, height) };
}

/** The most children standing in the Dormant valley's line at once. */
export const LINEUP = 5;

/** How a Dormant line is measured: one standing child's size and spacing. */
function lineupMeasure(width: number, height: number) {
  const phone = width < 700 && height > width;
  const scale = 1.34 * Math.max(0.85, Math.min(1.3, height / 941)) * (phone ? 0.78 : 1);
  const w = drawWidth(BY_ID.standing!, scale, width, height);
  // A standing child's body is about half their drawn width; a step of 1.3
  // widths leaves a clear gap between neighbours, so nobody touches.
  const step = w * 1.3;
  const fit = Math.max(1, Math.floor((width * 0.92 - w) / step) + 1);
  return { phone, scale, w, step, fit };
}

/** How many stand in the line on this screen: five at most, fewer on a phone. */
export function lineupRoom(width: number, height: number): number {
  return Math.min(LINEUP, lineupMeasure(width, height).fit);
}

/**
 * THE DORMANT VALLEY (2026-10-13). With nothing in the fund nobody is out
 * playing yet: the children stand side by side on the meadow, front on,
 * waiting for it to come back -- five at most, spaced so they never touch,
 * and a bigger family takes turns as it does in a living valley. Activities
 * resume at Recovering.
 */
export function lineup<P extends { id: string; name?: string }>(
  people: P[],
  width: number,
  height: number,
  turn = 0,
): { shown: P[]; assignments: Map<string, Activity>; placed: Placed[] } {
  const { phone, scale, w, step } = lineupMeasure(width, height);
  const room = Math.min(lineupRoom(width, height), people.length);
  const shown: P[] = [];
  for (let i = 0; i < room; i++) shown.push(people[(i + turn) % people.length]!);
  const standing = BY_ID.standing!;
  const y = height * (phone ? 0.8 : 0.83);
  const start = width / 2 - ((room - 1) * step) / 2;
  const placed: Placed[] = shown.map((person, i) => ({
    id: person.id,
    activity: standing,
    x: start + i * step,
    y,
    scale,
    station: STATIONS.meadow!,
    width: w,
  }));
  return { shown, assignments: new Map(shown.map((person) => [person.id, standing])), placed };
}
